/**
 * ⌘K palette provider registry. WS4 owns the shell; WS5/WS6 register their
 * providers (map nodes, flows, surfaces, files…) through
 * `registerPaletteProvider`. The provider/result/action shapes live HERE
 * since the 2026-08-28 redesign retired them from the wire contract
 * (they were never wire types — api/types.ts mirrors models.py only).
 */

import { create } from 'zustand';


export type PaletteResultKind =
  | 'dir'
  | 'flow'
  | 'step'
  | 'service'
  | 'queue'
  | 'schedule'
  | 'external'
  | 'surface'
  | 'file'
  | 'ask'
  | 'command';

/** What executing a palette result does — a serializable action descriptor. */
export type PaletteAction =
  /** the ask-the-guide fallback: sends `text` to the composer */
  | { type: 'ask-guide'; text: string }
  | { type: 'navigate'; to: string }
  | { type: 'run-command'; commandId: string };

export interface PaletteResult {
  /** unique within the provider */
  id: string;
  label: string;
  /** secondary line (mono path, glance, etc.) */
  hint?: string;
  kind: PaletteResultKind;
  action: PaletteAction;
}

export interface PaletteProvider {
  /** unique provider id, e.g. "nodes", "flows", "files", "ask" */
  id: string;
  /** group heading in the palette */
  label: string;
  /** lower renders first; default 100 */
  priority?: number;
  getResults(
    query: string,
    signal?: AbortSignal,
  ): PaletteResult[] | Promise<PaletteResult[]>;
}

interface PaletteRegistryState {
  providers: Record<string, PaletteProvider>;
  register: (provider: PaletteProvider) => void;
  unregister: (id: string) => void;
}

export const usePaletteRegistry = create<PaletteRegistryState>((set) => ({
  providers: {},
  register(provider) {
    set((s) => ({ providers: { ...s.providers, [provider.id]: provider } }));
  },
  unregister(id) {
    set((s) => {
      const next = { ...s.providers };
      delete next[id];
      return { providers: next };
    });
  },
}));

/**
 * Register a provider; returns the unregister function. Safe to call from
 * module setup or an effect (idempotent by provider id).
 */
export function registerPaletteProvider(provider: PaletteProvider): () => void {
  usePaletteRegistry.getState().register(provider);
  return () => usePaletteRegistry.getState().unregister(provider.id);
}

export function getPaletteProviders(): PaletteProvider[] {
  return Object.values(usePaletteRegistry.getState().providers).sort(
    (a, b) => (a.priority ?? 100) - (b.priority ?? 100),
  );
}

/** A provider group's resolved results, in render order. */
export interface PaletteGroup {
  providerId: string;
  label: string;
  results: PaletteResult[];
}

/**
 * Query every registered provider (async-tolerant). Providers that throw or
 * are aborted contribute nothing — the palette never breaks on one provider.
 */
export async function queryPaletteProviders(
  query: string,
  signal?: AbortSignal,
): Promise<PaletteGroup[]> {
  const providers = getPaletteProviders();
  const settled = await Promise.allSettled(
    providers.map(async (p) => ({
      providerId: p.id,
      label: p.label,
      results: await p.getResults(query, signal),
    })),
  );
  const groups: PaletteGroup[] = [];
  for (const outcome of settled) {
    if (outcome.status === 'fulfilled' && outcome.value.results.length > 0) {
      groups.push(outcome.value);
    }
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Action handlers — defaults for navigate / ask-guide / run-command. The
// canvas actions retired with the map (DECISIONS #62); a feature may
// override any handler while its surface is mounted.
// ---------------------------------------------------------------------------

export type PaletteActionHandler = (action: PaletteAction) => void;

const actionHandlers = new Map<PaletteAction['type'], PaletteActionHandler>();

export function registerPaletteActionHandler(
  type: PaletteAction['type'],
  handler: PaletteActionHandler,
): () => void {
  actionHandlers.set(type, handler);
  return () => {
    if (actionHandlers.get(type) === handler) actionHandlers.delete(type);
  };
}

export function runPaletteAction(action: PaletteAction): boolean {
  const handler = actionHandlers.get(action.type);
  if (handler === undefined) return false;
  handler(action);
  return true;
}

// ---------------------------------------------------------------------------
// Command registry (the `run-command` action target)
// ---------------------------------------------------------------------------

export interface PaletteCommand {
  id: string;
  run: () => void;
}

const commands = new Map<string, PaletteCommand>();

export function registerPaletteCommand(command: PaletteCommand): () => void {
  commands.set(command.id, command);
  return () => {
    if (commands.get(command.id) === command) commands.delete(command.id);
  };
}

export function runPaletteCommand(commandId: string): boolean {
  const command = commands.get(commandId);
  if (command === undefined) return false;
  command.run();
  return true;
}

/**
 * Whether a command is registered RIGHT NOW. Providers gate their command
 * entries on this so the palette never offers a row whose Enter would be a
 * silent no-op (runPaletteCommand on an unknown id returns false and nothing
 * visible happens).
 */
export function hasPaletteCommand(commandId: string): boolean {
  return commands.has(commandId);
}
